import mapboxgl, { LngLatLike } from "mapbox-gl";
import { useEffect, useRef } from "react";
import { useMaps } from "../contexts/MapsContext";
import "mapbox-gl/dist/mapbox-gl.css";
import TAAudioAgent, { RecommendationData } from "ta-agent-sdk";
import { SelectedRecommendation } from "./SelectedRecommendation";
import { RecommendationList } from "./RecommendationList";
import { useMediaQuery } from "@mui/material";
// mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!;

export const INITIAL_CENTER = [-115.1391, 36.1716];
export const INITIAL_ZOOM = 10;
export function Map({
  agent,
  recommendations,
  showChat,
}: {
  agent: TAAudioAgent;
  recommendations: RecommendationData[];
  showChat: boolean;
}) {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const {
    map,
    setMap,
    addMarkerChat,
    flyMapTo,
    resetMarkersChat,
    setAgent,
    is3D,
    set3D,
    selectedRecommendation,
    closeDetails,
  } = useMaps();
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // const cycleIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const mapLocal = new mapboxgl.Map({
      accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN!,
      container: mapContainerRef.current,
      // style: "mapbox://styles/devikak/cm9imsbuu00r201r3fsxe6o85",
      // @ts-ignore - mapbox-gl v3 supports 'config'
      // config: {
      //   basemap: {
      //     lightPreset: "dusk",
      //     showPointOfInterestLabels: false,
      //   },
      // },
      center: INITIAL_CENTER as LngLatLike,
      zoom: INITIAL_ZOOM,
      pitch: 0,
      localFontFamily: "TripSansRegular",
    });
    mapLocal.addControl(new mapboxgl.NavigationControl(), "top-left");
    setMap(mapLocal);
    setAgent(agent);

    return () => {
      // Clear context map reference before removing to avoid downstream resize calls
      setMap(null);
      try {
        mapLocal.remove();
      } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetMarkersChat();
    const filtered = recommendations.filter(
      (x) =>
        ("location" in x &&
          x.location.coordinates?.latitude &&
          x.location.coordinates?.longitude) ||
        ("experience" in x &&
          x.experience.coordinates?.latitude &&
          x.experience.coordinates?.longitude)
    );
    filtered.map((x) => addMarkerChat(x));
    if (filtered.length === 0) return;

    const firstRecommendation = filtered[0];
    if (
      "location" in firstRecommendation &&
      firstRecommendation.location?.coordinates &&
      firstRecommendation.location?.coordinates.latitude &&
      firstRecommendation.location?.coordinates.longitude
    ) {
      flyMapTo(
        firstRecommendation.location.coordinates.longitude,
        firstRecommendation.location.coordinates.latitude
      );
    } else if (
      "experience" in firstRecommendation &&
      firstRecommendation.experience?.coordinates &&
      firstRecommendation.experience?.coordinates.latitude &&
      firstRecommendation.experience?.coordinates.longitude
    ) {
      flyMapTo(
        firstRecommendation.experience.coordinates.longitude,
        firstRecommendation.experience.coordinates.latitude
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendations]);

  // Resize the map when the panel opens/closes so Mapbox reflows correctly
  useEffect(() => {
    if (!map) return;
    // Next frame to ensure DOM has reflowed
    let cancelled = false;
    const id = window.requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        // Guard: skip if map was removed (internal canvas/style undefined)
        if ((map as any)?._canvas || (map as any)?.resize) {
          map.resize();
        }
      } catch {}
    });
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(id);
    };
  }, [map, selectedRecommendation]);

  return (
    <div className="h-full w-full top-0 left-0">
      <div className="h-full w-full flex">
        {/* Map area */}
        <div className="relative flex-1">
          <div ref={mapContainerRef} className="w-full h-full z-0" />

          {/* 2D/3D Toggle (stays inside the map area) */}
          <div
            className={`absolute top-4 ${
              recommendations.length > 0 && isDesktop && !selectedRecommendation
                ? "right-[390px]"
                : "right-4"
            } z-[99] flex gap-2 justify-self-end items-center`}
          >
            {/* <PrivacyBanner /> */}
            <button
              onClick={() => set3D(!is3D, true)}
              className="bg-white/90 backdrop-blur text-black rounded-md px-3 py-2 shadow hover:shadow-md"
            >
              {is3D ? "3D" : "2D"}
            </button>
          </div>

          {!selectedRecommendation && (
            <RecommendationList
              recommendations={recommendations}
              showChat={showChat}
            />
          )}
        </div>

        {/* Right-side details panel */}
        {selectedRecommendation && (
          <SelectedRecommendation
            agent={agent}
            closeDetails={closeDetails}
            selectedRecommendation={selectedRecommendation}
          />
        )}
      </div>
    </div>
  );
}
